const FALLBACK_LOCALE = 'en';

type MaybeLocalized = object | string | string[] | null | undefined;

/** LocalizedString — интерфейс без индексной подписи, поэтому берём объект дженериком */
const pick = <T,>(value: object | null | undefined, key: string): T | undefined =>
  (value as Record<string, T> | null | undefined)?.[key];

/**
 * Незаполненная локаль в мутаторе не должна давать пустой экран —
 * откатываемся на en вместо `value[locale] === undefined`.
 *
 * В мутаторе часть параметров хранится нелокализованной строкой там, где тип
 * обещает LocalizedString (например email.title, ltsPhotoExample.photoProgressTitle).
 * Такое значение отдаём как есть — иначе `"текст"[locale]` рендерился как undefined.
 */
export const getLocalizedText = (value: MaybeLocalized, locale: string): string => {
  if (typeof value === 'string') {
    return value;
  }

  const localized = pick<string>(value as object, locale);

  if (typeof localized === 'string' && localized.trim()) {
    return localized;
  }

  const fallback = pick<string>(value as object, FALLBACK_LOCALE);

  return typeof fallback === 'string' ? fallback : '';
};

export const getLocalizedList = (value: MaybeLocalized, locale: string): string[] => {
  if (Array.isArray(value)) {
    return value;
  }

  const localized = pick<string[]>(value as object, locale);

  if (Array.isArray(localized) && localized.length) {
    return localized;
  }

  const fallback = pick<string[]>(value as object, FALLBACK_LOCALE);

  return Array.isArray(fallback) ? fallback : [];
};
